// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/RuleDetailPanel.test.tsx
/**
 * What a citation is CALLED on a URL, and which citation a URL meant.
 *
 * Its own module, and not the panel that resolves it, for two reasons: the
 * chat/review citation chip needs the same definition and must not import a
 * component to get it (the QA tsconfig compiles these files without `--jsx`),
 * and a link that builds the value while a panel builds the match is two
 * definitions that drift.
 */

import {
  type KnowledgeRuleResult,
  type KnowledgeRuleCitationResult,
} from "../../../../utils/parsers/knowledge";

/**
 * How a citation names itself on a URL: its cell range, or its page.
 *
 * ONE value for both source kinds, because the reader's need is the same --
 * open the source where the claim was read from -- and a second param for the
 * pdf case would be a second thing to forget. A workbook citation answers with
 * its A1 range (rembrandt hardcodes `row: null`, so `range` is the only cell
 * key there is); a page citation answers with its page number.
 *
 * Empty when the citation is anchored to neither, which is a quote-only
 * citation: there is nowhere to open it, and a link that claimed otherwise
 * would land the reader at the top of the file.
 */
export function citationAnchorParam(
  // Structural, not `Pick<KnowledgeRuleCitationResult>`: the same function
  // serves the REVIEW comment's citation, whose `location` is optional where
  // the rule's is required. Narrowing to one of them would fork the definition.
  citation:
    | { location?: KnowledgeRuleCitationResult["location"] }
    | null
    | undefined,
): string {
  const range = citation?.location?.cellAnchor?.range?.trim();
  if (range) return range;
  const page = citation?.location?.pageNumber;
  return page != null ? String(page) : "";
}

/**
 * The citation a deep link asked for.
 *
 * Keyed on the ANCHOR and not on `sourceId`: one rule can cite the SAME source
 * at several rows, so the source alone opens an arbitrary one of them. Returns
 * null when nothing matches, which leaves the panel behaving as it does for a
 * link that carries no citation at all.
 */
export function citationAtAnchor(
  rule: KnowledgeRuleResult | undefined,
  anchor: string | undefined,
): KnowledgeRuleCitationResult | null {
  const wanted = (anchor ?? "").trim().toUpperCase();
  if (!rule || !wanted) return null;
  const found = (rule.citations ?? []).find(
    (citation) => citationAnchorParam(citation).toUpperCase() === wanted,
  );
  return found ?? null;
}
